import { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, ArrowRight, Sparkles, CalendarCheck } from 'lucide-react';
import { PACKAGES } from '../lib/pricing';
import { useOrder } from '../context/OrderContext';
import SectionHead from './SectionHead';
import WaitlistDialog from './WaitlistDialog';
import { Reveal, Stagger, Item } from '../lib/motion';

export default function PackagesSection() {
  const { selectPackage, selectBeratung, selectFounder, order } = useOrder();
  const selected = order.kind === 'package' ? order.selectionId : null;
  const [slots, setSlots] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/founder-slots`)
      .then(({ data }) => { if (active) setSlots(data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const soldOut = slots ? slots.remaining === 0 : false;

  return (
    <section id="pakete" data-testid="packages-section" className="chapter bg-[#16181D]/40">
      <div className="wrap">
        <SectionHead
          n="04"
          eyebrow="Monatspakete"
          title="Fünf Pakete. Ein Prinzip: Sie zahlen für Sichtkontakte im Kaufmoment."
          testid="packages-headline"
          lead="Für Partner aus der Region – Gastronomie, Handwerk, Autohaus, Fitnessstudio, Bank. Alle Preise netto pro Monat, Laufzeit ab einem Monat. Auswahl übernehmen wir direkt in Ihren Auftrag."
        />

        <Stagger className="mt-16 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="packages-grid" gap={0.07}>
          {PACKAGES.map((p) => {
            const isPremium = p.id === 'premium';
            const isSelected = selected === p.id;
            return (
              <Item
                key={p.id}
                as="article"
                data-testid={`package-card-${p.id}`}
                className={`group relative flex flex-col rounded-2xl border p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 sm:p-8 ${
                  isPremium ? 'border-[#FF6B00] bg-[#1E222A] shadow-2xl shadow-[#FF6B00]/10' : 'border-[#282C37] bg-[#16181D] hover:border-[#FF6B00]/60'
                } ${isSelected ? 'ring-2 ring-[#FF6B00] ring-offset-2 ring-offset-[#0F1012]' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-2xl font-extrabold tracking-tight">{p.name}</h3>
                  {p.badge && <span className="rounded-full bg-[#FF6B00] px-3 py-1 font-mono-num text-[10px] font-bold uppercase tracking-widest text-white">{p.badge}</span>}
                  {isSelected && (
                    <span className="rounded-full bg-white px-3 py-1 font-mono-num text-[10px] font-bold uppercase tracking-widest text-[#0F1012]" data-testid={`package-selected-${p.id}`}>
                      Ausgewählt
                    </span>
                  )}
                </div>
                <p className="t-muted mt-2 min-h-[3.5rem]">{p.audience}</p>
                <p className="mt-6 flex items-baseline gap-2">
                  <span className="font-mono-num text-5xl font-bold tracking-tighter text-white">{p.price.toLocaleString('de-DE')} €</span>
                  <span className="text-xs text-[#7D8391]">netto / Monat</span>
                </p>
                <dl className="mt-7 flex-1 space-y-3 border-t border-[#282C37] pt-6 text-sm">
                  <div className="flex gap-3"><dt className="w-16 shrink-0 text-[#7D8391]">Zone</dt><dd className="text-[#F9FAFB]">{p.zone}</dd></div>
                  <div className="flex gap-3"><dt className="w-16 shrink-0 text-[#7D8391]">Spot</dt><dd className="text-[#F9FAFB]">{p.spot}</dd></div>
                  <div className="flex gap-3"><dt className="w-16 shrink-0 text-[#7D8391]">Kontakte</dt><dd className="text-[#F9FAFB]">{p.contacts}</dd></div>
                  {p.extras && <div className="flex gap-3"><dt className="w-16 shrink-0 text-[#7D8391]">Inklusive</dt><dd className="text-[#F9FAFB]">{p.extras}</dd></div>}
                </dl>
                <p className="mt-5 font-mono-num text-[11px] uppercase tracking-wider text-[#7D8391]">{p.cpm}</p>
                <button
                  type="button"
                  data-testid={`package-select-${p.id}`}
                  onClick={() => selectPackage(p.id)}
                  className={`mt-6 ${isPremium ? 'btn-primary' : 'btn-ghost'} !px-5 !py-3`}
                >
                  {isSelected ? 'Ausgewählt' : 'Auswählen'} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </Item>
            );
          })}

          <Item
            as="article"
            data-testid="package-card-beratung"
            className={`flex flex-col rounded-2xl border border-dashed bg-[#16181D] p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 sm:p-8 ${
              order.kind === 'beratung' ? 'border-[#FF6B00] ring-2 ring-[#FF6B00] ring-offset-2 ring-offset-[#0F1012]' : 'border-[#3A3F4B] hover:border-[#FF6B00]/60'
            }`}
          >
            <CalendarCheck className="h-7 w-7 text-[#FF6B00]" />
            <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight">Nicht sicher, was passt?</h3>
            <p className="t-body mt-3 flex-1">
              Wir zeigen Ihnen die Bildschirme vor Ort, rechnen Ihre Sichtkontakte durch und schlagen ein Paket vor. 30 Minuten im Markt, ohne Verpflichtung.
            </p>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-mono-num text-5xl font-bold tracking-tighter text-white">0 €</span>
              <span className="text-xs text-[#7D8391]">Beratung vor Ort</span>
            </p>
            <button type="button" data-testid="package-select-beratung" onClick={selectBeratung} className="btn-ghost mt-6 !border-[#FF6B00] !px-5 !py-3 !text-[#FF6B00] hover:!bg-[#FF6B00] hover:!text-white">
              Beratung anfragen <ArrowRight className="h-4 w-4" />
            </button>
          </Item>
        </Stagger>

        <Reveal className="mt-14 overflow-hidden rounded-3xl bg-[#FF6B00] p-8 sm:p-12" data-testid="founder-banner">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="flex items-center gap-2 font-mono-num text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">
                <Sparkles className="h-4 w-4" /> Limitiert · {slots ? `${slots.total} Plätze` : '10 Plätze'}
              </p>
              <h3 className="mt-4 font-display text-3xl font-black tracking-tight text-white sm:text-4xl" data-testid="founder-headline">
                Gründungspartner: ein Jahr Vorsprung.
              </h3>
              {slots && (
                <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 font-mono-num text-xs font-bold uppercase tracking-[0.18em] text-white" data-testid="founder-slots">
                  <span className={`h-2 w-2 rounded-full ${soldOut ? 'bg-white/60' : 'bg-white animate-pulse'}`} />
                  {soldOut ? 'Alle Plätze belegt · Warteliste' : `Noch ${slots.remaining} von ${slots.total} Plätzen frei`}
                </p>
              )}
              <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-white/90">
                Die ersten zehn Partner aus der Region starten zu Sonderkonditionen und werden im Markt als Gründungspartner genannt. Gilt für zwölf Monate Laufzeit, solange Plätze frei sind.
              </p>
            </div>
            <div className="lg:col-span-5">
              <ul className="space-y-3 text-base font-semibold text-white">
                {['20 % Nachlass auf zwölf Monate', 'Branchenexklusivität inklusive', 'Spotproduktion Standard inklusive (Wert 490 €)', 'Feste Preise für das gesamte Jahr'].map((t) => (
                  <li key={t} className="flex gap-3"><Check className="mt-1 h-4 w-4 shrink-0" /> {t}</li>
                ))}
              </ul>
              <button
                type="button"
                data-testid="founder-cta"
                onClick={() => (soldOut ? setWaitlistOpen(true) : selectFounder())}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-[#E05E00] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-xl"
              >
                {soldOut ? 'Auf die Warteliste' : 'Als Gründungspartner starten'} <ArrowRight className="h-4 w-4" />
              </button>
              {soldOut && <p className="mt-3 text-sm font-semibold text-white/85" data-testid="founder-waitlist-hint">Aktuell sind alle Plätze belegt – wir nehmen Sie gern auf die Warteliste.</p>}
            </div>
          </div>
        </Reveal>
      </div>

      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} />
    </section>
  );
}
